import { useMemo, useState } from "react";
import {
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
  Cell,
} from "recharts";
import type { KLinePoint } from "@/lifekline/types";

interface LifeKLineChartProps {
  data: KLinePoint[];
  userName?: string;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: Array<{
    payload: KLinePoint;
  }>;
}

// Custom candlestick bar component
const CandlestickBar = (props: any) => {
  const { x, y, width, height, payload } = props;
  
  if (!payload) return null;
  
  const { open, close, high, low } = payload;
  const isUp = close >= open;
  const color = isUp ? "#22c55e" : "#ef4444"; // Green for up, Red for down
  
  // Calculate positions
  const barWidth = Math.max(width * 0.6, 2);
  const barX = x + (width - barWidth) / 2;
  
  // Scale factor based on score range (0-100)
  const scaleY = height / 100;
  const baseY = y + height;
  
  // Calculate candlestick body
  const bodyTop = baseY - Math.max(open, close) * scaleY;
  const bodyBottom = baseY - Math.min(open, close) * scaleY;
  const bodyHeight = Math.max(bodyBottom - bodyTop, 1);
  
  // Calculate wick
  const wickTop = baseY - high * scaleY;
  const wickBottom = baseY - low * scaleY;
  
  return (
    <g>
      {/* Wick (thin line from high to low) */}
      <line
        x1={x + width / 2}
        y1={wickTop}
        x2={x + width / 2}
        y2={wickBottom}
        stroke={color}
        strokeWidth={1}
      />
      {/* Body (rectangle from open to close) */}
      <rect
        x={barX}
        y={bodyTop}
        width={barWidth}
        height={bodyHeight}
        fill={color}
        stroke={color}
      />
    </g>
  );
};

// Custom tooltip
const CustomTooltip = ({ active, payload }: CustomTooltipProps) => {
  if (!active || !payload || !payload.length) return null;
  
  const data = payload[0]?.payload;
  if (!data) return null;
  
  const isUp = data.close >= data.open;
  
  return (
    <div className="bg-background/95 backdrop-blur-sm border border-border rounded-lg shadow-lg p-3 max-w-xs">
      <div className="font-semibold text-foreground mb-2">
        {data.year}年 {data.ganZhi} ({data.age}岁)
      </div>
      <div className="text-sm text-muted-foreground mb-2">
        大运：{data.daYun || "未知"}
      </div>
      <div className="grid grid-cols-2 gap-2 text-sm mb-2">
        <div className={isUp ? "text-green-500" : "text-red-500"}>
          开: {data.open} → 收: {data.close}
        </div>
        <div className="text-muted-foreground">
          高: {data.high} / 低: {data.low}
        </div>
      </div>
      <div className="text-sm text-foreground border-t border-border pt-2 mt-2">
        {data.reason}
      </div>
    </div>
  );
};

export function LifeKLineChart({ data, userName }: LifeKLineChartProps) {
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 30 });
  
  // Find peak year for highlighting
  const peakYear = useMemo(() => {
    if (!data || data.length === 0) return null;
    return data.reduce((max, point) => 
      point.high > (max?.high || 0) ? point : max
    , data[0]);
  }, [data]);
  
  // Get visible data based on range
  const visibleData = useMemo(() => {
    return data.slice(visibleRange.start, visibleRange.end);
  }, [data, visibleRange]);
  
  // Get unique DaYun periods for reference lines
  const daYunPeriods = useMemo(() => {
    const periods: { age: number; daYun: string }[] = [];
    let currentDaYun = "";
    
    for (const point of data) {
      if (point.daYun && point.daYun !== currentDaYun) {
        periods.push({ age: point.age, daYun: point.daYun });
        currentDaYun = point.daYun;
      }
    }
    
    return periods;
  }, [data]);
  
  // Range controls
  const handleRangeChange = (start: number) => {
    const end = Math.min(start + 30, data.length);
    setVisibleRange({ start: Math.max(0, start), end });
  };
  
  if (!data || data.length === 0) {
    return (
      <div className="h-96 flex items-center justify-center text-muted-foreground">
        暂无数据
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">
          {userName ? `${userName}的` : ""}人生流年大运K线图
        </h3>
        {peakYear && (
          <div className="text-sm text-muted-foreground">
            人生巅峰：{peakYear.year}年 ({peakYear.age}岁) - 评分 {peakYear.high}
          </div>
        )}
      </div>
      
      {/* Legend */}
      <div className="flex items-center gap-4 text-sm">
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 bg-green-500 rounded" />
          <span className="text-muted-foreground">运势上涨（吉）</span>
        </div>
        <div className="flex items-center gap-1">
          <span className="w-3 h-3 bg-red-500 rounded" />
          <span className="text-muted-foreground">运势下跌（凶）</span>
        </div>
      </div>
      
      {/* Chart */}
      <div className="h-96 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={visibleData} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
            <XAxis 
              dataKey="age" 
              tick={{ fill: 'currentColor', fontSize: 12 }}
              tickFormatter={(age) => `${age}岁`}
            />
            <YAxis
              domain={[0, 100]}
              tick={{ fill: 'currentColor', fontSize: 12 }}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* DaYun period reference lines */}
            {daYunPeriods
              .filter(p => p.age >= visibleRange.start && p.age <= visibleRange.end)
              .map((period, idx) => (
                <ReferenceLine
                  key={idx}
                  x={period.age}
                  stroke="#8b5cf6"
                  strokeDasharray="3 3"
                  label={{ value: period.daYun, position: 'top', fontSize: 10 }}
                />
              ))
            }
            
            {/* Peak year marker */}
            {peakYear && peakYear.age >= visibleRange.start && peakYear.age <= visibleRange.end && (
              <ReferenceLine
                x={peakYear.age}
                stroke="#fbbf24"
                strokeWidth={2}
                label={{ value: "★ 巅峰", position: 'top', fill: '#fbbf24' }}
              />
            )}
            
            {/* Candlestick bars */}
            <Bar
              dataKey="high"
              shape={<CandlestickBar />}
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      
      {/* Range slider */}
      <div className="flex items-center gap-4">
        <span className="text-sm text-muted-foreground">年龄范围：</span>
        <input
          type="range"
          min={0}
          max={Math.max(0, data.length - 30)}
          value={visibleRange.start}
          onChange={(e) => handleRangeChange(parseInt(e.target.value))}
          className="flex-1"
        />
        <span className="text-sm text-muted-foreground">
          {visibleRange.start + 1} - {visibleRange.end} 岁
        </span>
      </div>
      
      {/* Quick range buttons */}
      <div className="flex flex-wrap gap-2">
        {[
          { label: "童年 (1-20岁)", start: 0 },
          { label: "青年 (20-40岁)", start: 19 },
          { label: "中年 (40-60岁)", start: 39 },
          { label: "老年 (60-80岁)", start: 59 },
          { label: "晚年 (80-100岁)", start: 79 },
        ].map(({ label, start }) => (
          <button
            key={label}
            onClick={() => handleRangeChange(start)}
            className={`px-3 py-1 text-sm rounded-lg border transition-colors ${
              visibleRange.start === start
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background border-border hover:bg-accent"
            }`}
          >
            {label}
          </button>
        ))}
      </div>
    </div>
  );
}
